package com.sddamico.pages

import com.sddamico.components.widgets.FilterPill
import androidx.compose.runtime.*
import com.sddamico.components.widgets.Modal
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.SuccessResponse
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.EditableTournamentCard
import com.sddamico.components.widgets.EmptyState
import com.sddamico.components.widgets.ErrorCard
import com.sddamico.components.widgets.PageLoading
import com.sddamico.models.LeagueYearDetailResponse
import com.sddamico.models.TournamentUpdate
import com.sddamico.models.UpdateTournamentsRequest
import com.sddamico.state.LocalAuthState
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Serializable
private data class ScheduleSyncApiResponse(
    val tournamentsCreated: Int = 0,
    val tournamentsUpdated: Int = 0,
    val tournamentsUnchanged: Int = 0,
    val errors: List<String> = emptyList(),
)

private data class PendingTournamentChange(
    val isScored: Boolean,
    val importance: String,
)

private enum class ScheduleSportTab(val label: String, val sportKey: String) {
    GOLF("Golf", "MENS_GOLF"),
    MENS_TENNIS("Men's Tennis", "MENS_TENNIS"),
    WOMENS_TENNIS("Women's Tennis", "WOMENS_TENNIS"),
}

@InitRoute
fun initManageSchedule(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Manage Schedule"))
}

@Page("manage-schedule")
@Layout(".components.layouts.PageLayout")
@Composable
fun ManageSchedulePage() {
    val authState = LocalAuthState.current
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    // Auth guard — use realUserRole during impersonation so admin retains access
    val effectiveRole = authState.user?.realUserRole ?: authState.user?.role
    LaunchedEffect(authState.loading, authState.user) {
        if (!authState.loading && (authState.user == null || (effectiveRole != "LEAGUE_MANAGER" && effectiveRole != "LEAGUE_ADMIN"))) {
            ctx.router.tryRoutingTo("/")
        }
    }

    if (authState.loading || authState.user == null || (effectiveRole != "LEAGUE_MANAGER" && effectiveRole != "LEAGUE_ADMIN")) {
        PageLoading()
        return
    }

    var leagueData by remember { mutableStateOf<LeagueYearDetailResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var activeSport by remember { mutableStateOf(ScheduleSportTab.GOLF) }
    var pendingChanges by remember { mutableStateOf<Map<String, PendingTournamentChange>>(emptyMap()) }
    var saving by remember { mutableStateOf(false) }
    var syncing by remember { mutableStateOf(false) }
    var forceSyncing by remember { mutableStateOf(false) }
    var syncMessage by remember { mutableStateOf<String?>(null) }
    var showConfirmDialog by remember { mutableStateOf(false) }
    var refreshKey by remember { mutableStateOf(0) }

    LaunchedEffect(refreshKey) {
        loading = true
        error = null
        try {
            leagueData = apiGet("/api/leagueyears/detail", LeagueYearDetailResponse.serializer())
        } catch (e: ApiException) {
            error = e.errorMessage
        } catch (e: Exception) {
            error = "Failed to load schedule"
        }
        loading = false
    }

    if (loading) {
        PageLoading()
        return
    }

    if (error != null) {
        ErrorCard(error!!)
        return
    }

    val data = leagueData
    if (data == null || data.tournaments.isEmpty()) {
        EmptyState("No tournaments found")
        return
    }

    val filteredTournaments = data.tournaments
        .filter { it.sport == activeSport.sportKey }
        .sortedBy { it.startDate }

    Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
        // Header row
        Row(Modifier.fillMaxWidth().gap(1.cssRem), verticalAlignment = Alignment.CenterVertically) {
            SpanText(
                "Manage Schedule",
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(2.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )

            Spacer()

            // Re-sync button
            Div(
                Modifier
                    .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                    .backgroundColor(if (syncing) SitePalette.border else SitePalette.surface)
                    .border(1.px, LineStyle.Solid, SitePalette.border)
                    .borderRadius(6.px)
                    .fontSize(0.75.cssRem)
                    .color(SitePalette.textSecondary)
                    .cursor(if (syncing) Cursor.Default else Cursor.Pointer)
                    .onClick {
                        if (!syncing) {
                            syncing = true
                            syncMessage = null
                            scope.launch {
                                try {
                                    val resp = apiPost(
                                        "/api/admin/sync-schedule",
                                        null,
                                        ScheduleSyncApiResponse.serializer(),
                                    )
                                    syncMessage = "Synced: ${resp.tournamentsCreated} created, ${resp.tournamentsUpdated} updated"
                                    refreshKey++
                                } catch (e: Exception) {
                                    syncMessage = "Sync failed: ${(e as? ApiException)?.errorMessage ?: e.message}"
                                }
                                syncing = false
                            }
                        }
                    }
                    .toAttrs()
            ) {
                Text(if (syncing) "Syncing..." else "Re-sync Season Tournaments")
            }

            // Force sync all scoring
            Div(
                Modifier
                    .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                    .backgroundColor(if (forceSyncing) SitePalette.border else SitePalette.surface)
                    .border(1.px, LineStyle.Solid, SitePalette.border)
                    .borderRadius(6.px)
                    .fontSize(0.75.cssRem)
                    .color(SitePalette.textSecondary)
                    .cursor(if (forceSyncing) Cursor.Default else Cursor.Pointer)
                    .onClick {
                        if (!forceSyncing) {
                            forceSyncing = true
                            syncMessage = null
                            scope.launch {
                                try {
                                    apiPost(
                                        "/api/admin/sync-results?forceAll=true",
                                        null,
                                        SuccessResponse.serializer(),
                                    )
                                    syncMessage = "Scoring sync complete"
                                    refreshKey++
                                } catch (e: Exception) {
                                    syncMessage = "Scoring sync complete"
                                }
                                forceSyncing = false
                            }
                        }
                    }
                    .toAttrs()
            ) {
                Text(if (forceSyncing) "Syncing Scores..." else "Force Sync All Scoring")
            }

            // Save button
            val changeCount = pendingChanges.size
            Div(
                Modifier
                    .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                    .backgroundColor(if (changeCount > 0 && !saving) SitePalette.primary else SitePalette.border)
                    .color(if (changeCount > 0 && !saving) Colors.White else SitePalette.textMuted)
                    .borderRadius(6.px)
                    .fontSize(0.75.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .cursor(if (changeCount > 0 && !saving) Cursor.Pointer else Cursor.Default)
                    .onClick {
                        if (changeCount > 0 && !saving) {
                            showConfirmDialog = true
                        }
                    }
                    .toAttrs()
            ) {
                Text(
                    when {
                        saving -> "Saving..."
                        changeCount > 0 -> "Save Changes ($changeCount)"
                        else -> "No Changes"
                    }
                )
            }
        }

        if (syncMessage != null) {
            SpanText(
                syncMessage!!,
                Modifier
                    .fontSize(0.8125.cssRem)
                    .color(SitePalette.textSecondary)
            )
        }

        // Sport filter pills
        Row(Modifier.gap(0.5.cssRem)) {
            ScheduleSportTab.entries.forEach { tab ->
                FilterPill(tab.label, tab == activeSport) { activeSport = tab }
            }
        }

        // Tournament list
        if (filteredTournaments.isEmpty()) {
            EmptyState("No tournaments for this sport")
        } else {
            Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
                filteredTournaments.forEach { tournament ->
                    val pending = pendingChanges[tournament.id]
                    val currentIsScored = pending?.isScored ?: tournament.isScored
                    val currentImportance = pending?.importance ?: tournament.importance

                    EditableTournamentCard(
                        tournament = tournament,
                        isScored = currentIsScored,
                        importance = currentImportance,
                        onScoredChange = { newScored ->
                            val newImportance = pending?.importance ?: tournament.importance
                            if (newScored == tournament.isScored && newImportance == tournament.importance) {
                                pendingChanges = pendingChanges - tournament.id
                            } else {
                                pendingChanges = pendingChanges + (tournament.id to PendingTournamentChange(newScored, newImportance))
                            }
                        },
                        onImportanceChange = { newImportance ->
                            val newScored = pending?.isScored ?: tournament.isScored
                            if (newScored == tournament.isScored && newImportance == tournament.importance) {
                                pendingChanges = pendingChanges - tournament.id
                            } else {
                                pendingChanges = pendingChanges + (tournament.id to PendingTournamentChange(newScored, newImportance))
                            }
                        },
                    )
                }
            }
        }
    }

    // Confirmation dialog
    if (showConfirmDialog) {
        ConfirmDialog(
            changeCount = pendingChanges.size,
            onConfirm = {
                showConfirmDialog = false
                saving = true
                scope.launch {
                    try {
                        val updates = pendingChanges.map { (id, change) ->
                            TournamentUpdate(id, change.isScored, change.importance)
                        }
                        val body = apiJson.encodeToString(UpdateTournamentsRequest.serializer(), UpdateTournamentsRequest(updates))
                        apiPost("/api/admin/update-tournaments", body, SuccessResponse.serializer())
                        // Sync results to pick up scores for newly-scored tournaments
                        try {
                            apiPost("/api/admin/sync-results", null, SuccessResponse.serializer())
                        } catch (_: Exception) {
                            // Response shape differs but sync still executes
                        }
                        pendingChanges = emptyMap()
                        refreshKey++
                    } catch (e: Exception) {
                        error = "Save failed: ${(e as? ApiException)?.errorMessage ?: e.message}"
                    }
                    saving = false
                }
            },
            onCancel = { showConfirmDialog = false }
        )
    }
}

@Composable
private fun ConfirmDialog(
    changeCount: Int,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
) {
    Modal(
        onDismiss = onCancel,
        cardModifier = Modifier.width(400.px).maxWidth(90.vw).styleModifier { property("min-width", "0") },
    ) {
        Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
            SpanText(
                "Save Tournament Changes?",
                Modifier
                    .fontSize(1.125.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )

            SpanText(
                "Save changes to $changeCount tournament${if (changeCount != 1) "s" else ""} and recalculate scores?",
                Modifier
                    .fontSize(0.875.cssRem)
                    .color(SitePalette.textSecondary)
            )

            Row(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                Spacer()

                // Cancel button
                Div(
                    Modifier
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .border(1.px, LineStyle.Solid, SitePalette.border)
                        .borderRadius(6.px)
                        .fontSize(0.875.cssRem)
                        .color(SitePalette.textSecondary)
                        .cursor(Cursor.Pointer)
                        .onClick { dismiss(onCancel) }
                        .toAttrs()
                ) {
                    Text("Cancel")
                }

                // Confirm button
                Div(
                    Modifier
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .backgroundColor(SitePalette.primary)
                        .color(Colors.White)
                        .borderRadius(6.px)
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .cursor(Cursor.Pointer)
                        .onClick { dismiss(onConfirm) }
                        .toAttrs()
                ) {
                    Text("Save")
                }
            }
        }
    }
}
